import LabelField from '@app/components/atoms/LabelField/LabelField'
import Section from '@app/components/atoms/Section/Section'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import UserCreateSchema from '../UserCreate/UserCreateSchema'

const ButtonCustom = styled(Button)({
  marginTop: 20,
  marginRight: 20,
})

export default function UserEdit({ userInfo, roles, partners, merchants }) {
  const { t } = useTranslation()
  const [role, setRole] = useState(userInfo.role)

  const initialValues = {
    ...userInfo,
    partner_id: userInfo.partner_id || '',
    merchant_id: userInfo.merchant_id || '',
  }

  const onSubmit = (data) => {
    Inertia.put(route('admin.users.update', { id: userInfo.id }), data, {})
  }

  const {
    register,
    trigger,
    handleSubmit,
    getValues,
    control,
    setValue,
    formState: { errors },
    setError,
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: yupResolver(UserCreateSchema),
  })

  return (
    <Grid container component="form" spacing={2} onSubmit={handleSubmit(onSubmit)}>
      <Grid item xs={12}>
        <Section title={t('USER.BASIC_INFORMATION')}>
          <Grid container>
            <Grid item lg={7} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelField required title={t('USER.NAME')}>
                    <TextField
                      fullWidth
                      name="name"
                      {...register('name')}
                      error={errors.name ? true : false}
                      helperText={errors.name?.message}
                      size="small"
                      type="text"
                      variant="outlined"
                      onFocus={() => trigger('name')}
                    />
                  </LabelField>
                </Grid>
                <Grid item xs={12}>
                  <LabelField required title={t('USER.EMAIL')}>
                    <TextField
                      fullWidth
                      name="email"
                      size="small"
                      type="text"
                      {...register('email')}
                      disabled
                      error={
                        (errors.email && 0 === getValues('email').length) || (errors.email && getValues('email').length)
                          ? true
                          : false
                      }
                      helperText={errors.email ? errors.email?.message : ''}
                      variant="outlined"
                      onFocus={() => trigger('email')}
                    />
                  </LabelField>
                </Grid>

                <Grid item xs={12}>
                  <LabelField required title={t('USER.PHONE')}>
                    <TextField
                      fullWidth
                      {...register('phone')}
                      error={errors.phone ? true : false}
                      helperText={errors.phone?.message}
                      name="phone"
                      size="small"
                      variant="outlined"
                      onFocus={() => trigger('phone')}
                    />
                  </LabelField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Section title={t('USER.ROLE')}>
          <Grid container>
            <Grid container item lg={7} spacing={2} xs={12}>
              <Grid item xs={12}>
                <LabelField required title={t('USER.ROLE')}>
                  <Controller
                    control={control}
                    name="role"
                    render={() => (
                      <TextField
                        fullWidth
                        select
                        InputLabelProps={{
                          shrink: true,
                        }}
                        name="role"
                        size="small"
                        value={getValues('email').length ? getValues('role') : ''}
                        variant="outlined"
                        onChange={(e) => {
                          setValue('role', e.target.value)
                          setRole(e.target.value)
                        }}
                        onFocus={() => trigger('role')}
                      >
                        {roles.map((option) => (
                          <MenuItem
                            disabled={getValues('email').length ? false : true}
                            key={option.value}
                            value={option.value}
                          >
                            {t(option.name)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </LabelField>
              </Grid>

              <Grid
                item
                style={{ display: 'partner' === role && getValues('email').length ? 'block' : 'none' }}
                xs={12}
              >
                <LabelField required title={t('USER.PARTNER')}>
                  <Autocomplete
                    fullWidth
                    defaultValue={getValues('partner')}
                    getOptionLabel={(option) => option.name}
                    options={partners}
                    renderInput={(params) => (
                      <TextField
                        fullWidth
                        {...params}
                        error={!!errors.partner && !getValues('partner').length}
                        helperText={errors.partner && !getValues('partner').length ? errors.partner?.message : ''}
                        variant="outlined"
                        {...register('partner')}
                        onFocus={() => trigger('partner')}
                      />
                    )}
                    size="small"
                    onChange={(e, value) => {
                      if (!value) {
                        setError('partner', { type: 'custom', message: t('VALIDATE.REQUIRED_PARTNER') })
                      }
                      setValue('partner_id', value?.id ?? '')
                      setValue('partner', value?.id ?? '')
                    }}
                  />
                </LabelField>
              </Grid>

              <Grid
                item
                style={{ display: 'merchant' === role && getValues('email').length ? 'block' : 'none' }}
                xs={12}
              >
                <LabelField required title={t('USER.MERCHANT')}>
                  <Autocomplete
                    fullWidth
                    defaultValue={getValues('merchant')}
                    getOptionLabel={(option) => option.name}
                    options={merchants}
                    renderInput={(params) => (
                      <TextField
                        fullWidth
                        {...params}
                        error={!!errors.merchant && !getValues('merchant').length}
                        helperText={errors.merchant && !getValues('merchant').length ? errors.merchant?.message : ''}
                        variant="outlined"
                        {...register('merchant')}
                        onFocus={() => trigger('merchant')}
                      />
                    )}
                    size="small"
                    onChange={(e, value) => {
                      if (!value) {
                        setError('merchant', { type: 'custom', message: t('VALIDATE.REQUIRED_MERCHANT') })
                      }
                      setValue('merchant_id', value?.id ?? '')
                      setValue('merchant', value?.id ?? '')
                    }}
                  />
                </LabelField>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs={12}>
        <ButtonCustom type="submit" variant="contained">
          {t('BUTTON.SAVE')}
        </ButtonCustom>
        <ButtonCustom color="inherit" component={InertiaLink} href={route('admin.users.index')} variant="contained">
          {t('BUTTON.CANCEL')}
        </ButtonCustom>
      </Grid>
    </Grid>
  )
}
